import { GoogleMap, InfoWindowF, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { environment } from 'environment';
import { ContactNet } from 'networking';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { classes, isEmpty } from 'shared';
import { useUser } from 'state';
import { Form, FormErrors, contactSchemas, getError, handleError, handleForm } from 'types';
import hero from '../assets/contact/hero-contact-min.jpg';
import { ContactReasonSelect } from '../components/form/contact-reason-select';
import { EmailInput } from '../components/form/email';
import { Input } from '../components/form/input';
import { PhoneInput } from '../components/form/phone';
import { GeneralTextArea } from '../components/form/textarea';
import { Button } from '../components/general/button';
import { A } from '../components/general/link';
import { BlueArch, WhiteArch } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';
import { tracker } from '../scripts/tracking';

const mapOptions: google.maps.MapOptions = {
  mapTypeControl: false,
  streetViewControl: false,
  disableDefaultUI: true,
  fullscreenControl: false,
  disableDoubleClickZoom: true,
  gestureHandling: 'none',
  keyboardShortcuts: false,
  panControl: false,
  rotateControl: false,
  scaleControl: false,
  scrollwheel: false,
  clickableIcons: false,
  styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
};

const mapCenter = { lat: 35.28540717332015, lng: -80.8431465731747 };

export default function ContactUs() {
  const { isLoaded } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey: environment.google.maps.embedKey });
  const [form, setForm] = useState<Form<typeof contactSchemas.contactUs>>({});
  const [errors, setErrors] = useState<FormErrors<typeof contactSchemas.contactUs>>(null);
  const [allError, setAllError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user } = useUser();

  const [markerOpen, setMarkerOpen] = useState(false);

  useEffect(() => {
    if (isEmpty(user)) return;
    setForm({ ...form, name: `${user.fname} ${user.lname}`, email: user.email, phone: user.phone });
  }, [user]);

  return (
    <main className="relative mb-12">
      <Meta title="contact" description="Contact Beds for Kids" noDefaultFooter />
      <section className="relative h-[70vh] w-full overflow-hidden">
        <Image
          alt="Beds for Kids Warehouse"
          src={hero}
          className="absolute z-0 h-full w-full object-cover"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>
      <section className="bg-white text-blue-500">
        <h1 className="mx-auto my-4 max-w-3xl text-center font-operetta text-6xl">need more information?</h1>
        <p className="mx-auto max-w-3xl px-4 pb-4 pt-8 text-center font-avenir text-xl leading-6">
          Do you have questions or need more information about how to get involved with Beds for Kids? Please use one of
          the options below.
        </p>
        <div className="mx-auto mt-8 grid max-w-3xl justify-center gap-4 px-4 lg:grid-cols-2 lg:gap-16">
          <div className="text-center">
            <h4 className="font-caliban text-5xl text-green-500">email us</h4>
            <A external href="mailto:info@bedsforkids.org" className="font-avenir text-2xl font-bold hover:underline">
              info@bedsforkids.org
            </A>
          </div>
          <div className="text-center">
            <h4 className="font-caliban text-5xl text-green-500">call us</h4>
            <A external href="tel:+19804221192" className="font-avenir text-2xl font-bold hover:underline">
              (980) 422-1192
            </A>
          </div>
        </div>
      </section>
      <section className="mt-16 bg-white text-blue-500">
        <div className="mx-auto mb-16 grid w-full max-w-5xl gap-12 md:w-auto md:grid-cols-5">
          <div className="aspect-[5/4] overflow-hidden md:col-span-3 md:rounded-2xl">
            {isLoaded && (
              <GoogleMap options={mapOptions} mapContainerClassName="w-full h-full" center={mapCenter} zoom={12}>
                <MarkerF position={mapCenter} onClick={() => setMarkerOpen(true)}>
                  {markerOpen && (
                    <InfoWindowF onCloseClick={() => setMarkerOpen(false)}>
                      <div>
                        <h1 className="text-lg font-semibold">Beds for Kids</h1>
                        <A
                          href="https://maps.app.goo.gl/LqDLU5MtH3mp8TkE8"
                          className="text-sm text-blue-500 hover:underline"
                          external>
                          <dd>
                            <p>2701-A Hutchison McDonald Road</p>
                            <p>Charlotte, NC 28269</p>
                          </dd>
                        </A>
                      </div>
                    </InfoWindowF>
                  )}
                </MarkerF>
              </GoogleMap>
            )}
          </div>
          <div className="flex flex-col items-center px-4 md:col-span-2 md:items-start md:pt-12">
            <div className="flex flex-col gap-6">
              <h4 className="font-caliban text-5xl text-green-500">visit us</h4>
              <div>
                <h6 className="font-avenir text-2xl font-bold">warehouse address</h6>
                <A
                  href="https://maps.app.goo.gl/LqDLU5MtH3mp8TkE8"
                  className="font-avenir text-lg font-medium leading-6 text-blue-500 hover:underline"
                  external>
                  <dd>
                    <p>2701-A Hutchison McDonald Road</p>
                    <p>Charlotte, NC 28269</p>
                  </dd>
                </A>
              </div>
              <div>
                <h6 className="font-avenir text-2xl font-bold">mailing address</h6>
                <A
                  href="https://goo.gl/maps/xvJugxPQS7P6ToQj7"
                  className="font-avenir text-lg font-medium leading-6 text-blue-500 hover:underline"
                  external>
                  <dd>
                    <p>1800 Camden Rd. Suite 107-17</p>
                    <p>Charlotte, NC 28203</p>
                  </dd>
                </A>
              </div>
            </div>
          </div>
        </div>
        <BlueArch />
      </section>
      <section className="bg-blue-500 text-white" id="form">
        <h1 className="mx-auto max-w-3xl py-4 text-center font-operetta text-5xl md:text-6xl">
          join the beds for kids home team
        </h1>
        {/* <p className="mx-auto max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          Fill out the following form and we will be in touch with you shortly.
        </p> */}
        <div className="mx-auto flex max-w-3xl flex-col gap-2 px-4 text-center font-avenir text-lg">
          <p className="mt-8">
            Whether you'd like to donate furniture, volunteer your time, host a fundraiser or partner with us, there's a
            place for you on the Beds for Kids Home Team. Choose how you'd like to get involved from the menu below, and
            the right person will be in touch soon.
          </p>
          <p className="mb-4 mt-6 italic">
            Seeking a bed for a child or family? Beds are provided through partner agency referrals. Please email{' '}
            <a href="mailto:klineberry@bedsforkids.org" className="font-semibold">
              klineberry@bedsforkids.org
            </a>{' '}
            rather than using this form.
          </p>
        </div>
      </section>
      <section className="flex justify-center bg-blue-500 text-white">
        <div className="mx-4 w-full max-w-3xl pt-8 lg:mx-auto">
          {!success ? (
            <>
              <form onSubmit={formSubmit} className="grid gap-4 text-blue-500 md:grid-cols-2">
                <div>
                  <label htmlFor="full-name" className="sr-only">
                    Contact Reason
                  </label>
                  <ContactReasonSelect
                    reason={form.reason}
                    onChange={v => handleForm(setForm, setErrors, errors, 'reason', v)}
                    error={getError(errors, 'reason')}
                    onChangeError={v => handleError(setErrors, 'reason', v)}
                    disabled={success}
                    placeholder="select inquiry reason"
                  />
                </div>
                <div>
                  <Input
                    name="Full Name"
                    autoComplete="name"
                    disabled={success}
                    placeholder="full name"
                    fKey="name"
                    form={form}
                    errors={errors}
                    setForm={setForm}
                    setErrors={setErrors}
                  />
                </div>
                <div>
                  <EmailInput
                    email={form.email}
                    onUpdate={v => handleForm(setForm, setErrors, errors, 'email', v)}
                    error={getError(errors, 'email')}
                    onUpdateError={v => handleError(setErrors, 'email', v)}
                    disabled={success}
                    placeholder="email"
                  />
                </div>
                <div>
                  <PhoneInput
                    phone={form.phone}
                    onUpdate={v => handleForm(setForm, setErrors, errors, 'phone', v)}
                    error={getError(errors, 'phone')}
                    onUpdateError={v => handleError(setErrors, 'phone', v)}
                    disabled={success}
                    placeholder="phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <GeneralTextArea
                    name="Message"
                    value={form.message}
                    onUpdate={v => handleForm(setForm, setErrors, errors, 'message', v)}
                    error={getError(errors, 'message')}
                    disabled={success}
                    rows={4}
                    placeholder="your message here"
                  />
                </div>
                {form.reason === 'FURNITURE_DONATION' && (
                  <div className={classes('text-center font-avenir text-lg text-white md:col-span-2')}>
                    <p className="text-large mb-4 mt-4 font-semibold">
                      If you are requesting a furniture pickup, please include your:
                    </p>
                    <ul className="mx-auto grid max-w-xl grid-cols-2 gap-1">
                      <li className="text-lg leading-6">First &amp; Last Name</li>
                      <li className="text-lg leading-6">Phone Number</li>
                      <li className="text-lg leading-6">Email Address</li>
                      <li className="text-lg leading-6">Address</li>
                      <li className="text-lg leading-6">City</li>
                      <li className="text-lg leading-6">Zip Code</li>
                    </ul>
                  </div>
                )}
                <div className={classes('md:col-span-2', !allError && 'hidden')}>
                  <p className="text-center text-red-500">
                    Something went wrong. Beds for Kids has been notified. Please try again later.
                  </p>
                </div>
                <div className="mt-8 text-center md:col-span-2">
                  <Button bType="tertiary" disabled={success} type="submit">
                    {success ? 'Sent!' : 'Submit'}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="text-center md:col-span-2">
                <h4 className="font-caliban text-5xl text-green-500">Thank you for reaching out to Beds for Kids!</h4>
                <p className="mt-4 text-center font-avenir text-lg">
                  We appreciate your patience and will get back to you as soon as possible.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );

  async function formSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (loading || success) return;

    const validate = contactSchemas.contactUs.safeParse(form);
    if (validate.success === false) return setErrors(validate.error.format());

    setLoading(true);
    const sent = await ContactNet.submit(validate.data);

    if (sent.success === false) return setAllError(true);

    setSuccess(true);
    setLoading(false);

    await tracker.trackContactUs(form.reason);
  }
}
